import { Injectable, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AgendaItemRightsList } from './agenda-rights';
import moment from 'moment';
import { addSeconds } from 'date-fns';
import { TimeslotsChangesProvider } from './timeslots-changes-provider';
import { forkJoin, from, Observable, of, switchMap } from 'rxjs';
import { AgendaCommentRow } from './agenda-comments-provider';
import { CurrentUser } from "../../common/providers/current-user";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { LangsProvider } from "../../common/providers/langs-provider";
import { AppDatePipe } from "../../common/pipes/app-date";
import { SessionRow, SessionsProvider } from "../../sessions/providers/sessions-provider";
import { Timeslot } from "../../common/models/timeslot";
import { Session } from "../../common/models/session";
import { Agenda } from "../../common/models/agenda";
import { Organization } from "../../common/models/organization";
import { catchError, map } from "rxjs/operators";
import { Paginator } from "../../common/models/paginator";
import { Week } from "../../common/models/week";

@Injectable({providedIn: 'root'})
export class TimeslotsProvider {
  public readonly CALENDAR_SESSION_DROPZONE_NAME: string = 'session-dropzone';
  @Input() public timeSegments = 2;
  private _todosCollectors: any[] = []; // a function that return a Promise<Notification>
  constructor(
    private api: ApiBaseService,
    private _utils: UtilsProvider,
    private _currentUser: CurrentUser,
    public translater: TranslateService,
    private _langsProvider: LangsProvider,
    private _datePipe: AppDatePipe,
    private _timeslotsChangesProvider: TimeslotsChangesProvider
  ) { }


  public getCalendarWeekData(organization: Organization, agendaId: number, week: Date, timeSegments: number): Observable<CalendarDayData[]> {
    const fromDate: Date = this._utils.firstDayOfWeek(week);
    const toDate: Date = new Date(this._utils.lastDayOfWeek(week).getTime() + (24 * 60 * 60 * 1000)); // include the end of the last day

    return this.getTimeslotsByAthleteAgenda({ agendaId: agendaId, fromDate: fromDate.toISOString(), toDate: toDate.toISOString() }).pipe(
      map((slots: Timeslot[]) => {
        const days: CalendarDayData[] = [];
        let currentDay: Date = new Date(fromDate);
        while (currentDay < toDate) {
          const nextDay: Date = new Date(currentDay.getTime() + (24 * 60 * 60 * 1000));
          const daySlots = slots.filter(slot => {
            // fromDate is a wall-clock time, but the API serializes it with a 'Z' (UTC)
            // suffix. Parse it the same way as createDaySegments (strip the timezone)
            // so that day-bucketing and segment-bucketing stay consistent. Without this,
            // sessions whose hour + timezone offset crosses midnight (e.g. 22:00 in CEST)
            // get assigned to the next day yet match no segment, and silently disappear.
            const slotDate = new Date(this._utils.ISODateToYmdHis(slot.fromDate ?? ''));
            return slotDate >= currentDay && slotDate < nextDay;
          });
          const segments = this.createDaySegments(currentDay, daySlots, timeSegments);
          days.push({
            date: currentDay,
            segments,
            timeSegments
          });
          currentDay = nextDay;
        }
        return days;
      }),
      catchError(error => {
        console.error('Error fetching week data:', error);
        return of([]);
      })
    );
  }

  private createDaySegments(date: Date, slots: Timeslot[], timeSegments: number): CalendarDaySegment[] {
    const segments: CalendarDaySegment[] = [];
    const increment = 60 / timeSegments * 60 * 1000; // ms
    let currentDate = new Date(date);

    while (currentDate < new Date(date.getTime() + (24 * 60 * 60 * 1000))) {
      const endDate = new Date(currentDate.getTime() + increment);
      const currentSlots = slots.filter(slot => {
        const slotDate = new Date(this._utils.ISODateToYmdHis(slot.fromDate!) ?? '');
        return slotDate >= currentDate && slotDate < endDate;
      });

      segments.push({
        time: currentDate,
        timeStr: this._datePipe.transform(currentDate, 'shortTime', undefined, this.translater.currentLang) ?? '',
        armyTimeStr: this._utils.DateToHm(currentDate),
        slots: currentSlots.map(slot => ({
          timeslot: slot,
          session: slot.session as any as SessionRow
        }))
      });

      currentDate = endDate;
    }
    return segments;
  }

  // REMOVED BECAUSE CALLING 7 TIMES API IS NOT IDEAL
  // public getCalendarDayData(organization: Organization, athleteUserId: number, date: Date, timeSegments: number): Observable<CalendarDayData> {
  //   let fromString: string = this._utils.DateToYmd(date);
  //   const toDate: Date = new Date(date);
  //   toDate.setDate(toDate.getDate() + 1);
  //   let toString: string = this._utils.DateToYmd(toDate);
  //   return this.getTimeslotsByAthlete({athleteUserId: athleteUserId, fromDate: fromString, toDate: toString}).pipe(
  //     map((slots: Timeslot[]) => {
  //       const segments: CalendarDaySegment[] = [];
  //       let _date: Date = new Date(date);
  //       const increment: number = 60 / timeSegments * 60 * 1000; // ms
  //
  //       while (_date.getTime() < toDate.getTime()) {
  //
  //         const slotsData: TimeslotRow[] = [];
  //         fromString = this._utils.DateToYmdHis(_date, false);
  //         const _toDate: Date = new Date(_date.getTime() + increment);
  //         toString = this._utils.DateToYmdHis(_toDate, false);
  //
  //         let i = 0;
  //         console.log("SLOTS LENGTH", slots.length)
  //         while (i < slots.length) {
  //           if (!slots[i].fromDate) break;
  //           if (slots[i].fromDate! >= fromString && slots[i].fromDate! < toString) {
  //
  //             // TODO FG: a slot must always have a session linked to it, right ?
  //             // in that case, we should not have to verify that the session exist
  //             // when building the dataset below
  //             console.log(`SLOTS[${i}] : ${slots[i]}`);
  //             if (slots[i].session) {
  //               slotsData.push({
  //                 timeslot: slots[i],
  //                 // session: this._utils.findIn(slots[i].sessionId, sessionRows)
  //                 session: slots[i].session as any as SessionRow
  //               });
  //             } else {
  //               console.log(slots[i]); // TODO FG: remove this when the TODO above will be fixed
  //             }
  //
  //             slots.splice(i, 1);
  //           } else {
  //             i++;
  //           }
  //         }
  //
  //         segments.push({
  //           time: _date,
  //           timeStr: this._datePipe.transform(_date, 'shortTime', undefined, this.translater.currentLang) ?? '',
  //           armyTimeStr: this._utils.DateToHm(_date),
  //           slots: slotsData
  //         });
  //
  //         // we add fake segments for the winter/summer time change
  //         const timezoneOffset = _date.getTimezoneOffset() - _toDate.getTimezoneOffset();
  //         if (timezoneOffset > 0) {
  //           for (i = 0; i < timezoneOffset / 60 * timeSegments; i++) {
  //             segments.push({
  //               time: _date,
  //               timeStr: this._datePipe.transform(_date, 'shortTime', undefined, this.translater.currentLang) ?? '',
  //               armyTimeStr: this._utils.DateToHm(_date),
  //               slots: []
  //             });
  //           }
  //         }
  //
  //         _date = _toDate;
  //       }
  //
  //       return {
  //         date,
  //         segments,
  //         timeSegments
  //       } as CalendarDayData;
  //     })
  //   );
  // }


  public getSessions(ids: number[]): Observable<Session[]> {
    if (!ids.length) return new Observable<Session[]>();
    return this.api.call('get','sessions/multiple/', JSON.stringify(ids), this._currentUser.getAccessToken()).pipe(
      map((result: Paginator) => {
        return Session.fromAPIList(result.data);
      }),
    );
  }

  public getTimeslotsByAthleteAgenda({agendaId, fromDate, toDate}: {agendaId: number, fromDate?: string, toDate?: string}): Observable<Timeslot[]> {
    return this.api.call('get', 'timeslots/', {
        agendaId : agendaId,
        fromDate : fromDate,
        toDate : toDate
      },
      this._currentUser.getAccessToken()
    ).pipe(
      map((result: Paginator) => {
        return Timeslot.fromAPIList(result.data);
      })
    );
  }

  public getTimeslotsByAthleteByWeek({athleteUserId, week}:{athleteUserId: number, week: Date}): Observable<Timeslot[]> {
    // Check for invalid date and use today as fallback
    const validWeek = (!week || isNaN(week.getTime())) ? new Date() : week;
    
    const fromDate: Date = this._utils.firstDayOfWeek(validWeek);
    const toDate: Date = new Date(this._utils.lastDayOfWeek(validWeek).getTime() + (24 * 60 * 60 * 1000)); // include the end of the last day

    return this.api.call('get', 'timeslots/', {
        userId : athleteUserId,
        fromDate : fromDate.toISOString(),
        toDate : toDate.toISOString()
      },
      this._currentUser.getAccessToken()
    ).pipe(
      map((result: Paginator) => {
        return Timeslot.fromAPIList(result.data);
      })
    );
  }

  public duplicateTimeslotsOnWeeks({organization, agenda, timeslotRows, weeks, overwriteOverlapping = false} : {organization: Organization, agenda: Agenda, timeslotRows: TimeslotRow[], weeks: WeekForDuplication[], overwriteOverlapping?: boolean}): Observable<any> {
    const payload = {
      overwriteOverlapping: overwriteOverlapping,
      weeks: weeks.map((week: WeekForDuplication) => week.fromDate),
      slots: timeslotRows.map((row: TimeslotRow) => {
        return {
          fromDate: row.timeslot?.fromDate,
          repetitionTag: row.timeslot?.repetitionTag,
          sessionId: row.session?.id
        }
      }),
    };
    return this.api.call('post', `organizations/${organization.id}/agendas/${agenda.id}/timeslots`, payload, this._currentUser.getAccessToken());
  }

  /*** END NEW REQUESTS ***/
  /*** ****************************** ***/
  /*** ******************************************** ***/
  /*** ****************************** ***/
  /*** OLD REQUESTS ***/




  public get(id: number): Observable<Timeslot> {
    return this.api.call('get', 'timeslots/' + id, {}, this._currentUser.getAccessToken());
  }

  public getSessionsByTimeslotDates(dates: string[]): Observable<Session[]> {
    return this.api.call('get','sessions/by-timeslot-dates/' + JSON.stringify(dates), {}, this._currentUser.getAccessToken());
  }


  public sessionRowToTimeSlotRow(sessionRow: SessionRow, startDate: Date): TimeslotRow {

    const toReturn: TimeslotRow = {
      timeslot: null,
      session: null,
      action: null
    };

    const timeslot = new Timeslot({});
    timeslot.fromDate = startDate.toString();
    timeslot.toDate = addSeconds(startDate, sessionRow.totalTime!).toString();
    timeslot.sessionId = sessionRow.id;

    toReturn.timeslot = timeslot;
    toReturn.session = sessionRow;
    toReturn.action = '';

    return toReturn;
  }

  // public getCalendarDayData(organization: Organization, agendaId: number, date: Date, timeSegments: number): Promise<CalendarDayData> {
  //     return new Promise<CalendarDayData>((resolve, reject) => {
  //         let fromString: string = this._utils.DateToYmd(date);
  //         const toDate: Date = new Date(date);
  //         toDate.setDate(toDate.getDate() + 1);
  //         let toString: string = this._utils.DateToYmd(toDate);
  // this._storage.select(Timeslot)
  //     .where('agendaId').equals(agendaId)
  //     .and('fromDate').greaterOrEqual(fromString)
  //     .and('fromDate').lower(toString)
  /**
   * !! Attention cette contrainte est nécessaire (cf. agends/components/calendar/calendar.js: createANewSession()
   * J'écarte volontairement les Timeslot ayant un id null ou à 0. Il s'agit
   * de Timeslot virtuel qui servent à déterminer les droits qu'un utilisateur
   * possède sur une séance issue d'un copier/coller. Ces Timeslot virtuel n'existe
   * que dans le storage.
   */
  // .and('id').greater(0)
  // .get()
  // .then((slots: Timeslot[]) => {
  //     this.getSessions(this._utils.getKeyValues(slots, 'sessionId'))
  //         .then((sessions: Session[]) => {
  //             const segments: CalendarDaySegment[] = [];
  //             let _date: Date = new Date(date);
  //             const increment: number = 60 / timeSegments * 60 * 1000; // ms
  //
  //             while (_date.getTime() < toDate.getTime()) {
  //                 const slotsData: TimeslotRow[] = [];
  //                 fromString = this._utils.DateToYmdHis(_date, false);
  //                 const _toDate: Date = new Date(_date.getTime() + increment);
  //                 toString = this._utils.DateToYmdHis(_toDate, false);
  //
  //                 let i = 0;
  //                 while (i < slots.length) {
  //                     if (!slots[i].fromDate) return;
  //                     if (slots[i].fromDate! >= fromString && slots[i].fromDate! < toString) {
  //                         const session = this._utils.findIn(
  //                             slots[i].sessionId, sessions
  //                         );
  //                         // TODO FG: a slot must always have a session linked to it, right ?
  //                         // in that case, we should not have to verify that the session exist
  //                         // when building the dataset below
  //                         if (session) {
  //                             slotsData.push({
  //                                 timeslot: slots[i],
  //                                 // session: this._utils.findIn(slots[i].sessionId, sessionRows)
  //                                 session
  //                             });
  //                         } else {
  //                             console.log(slots[i]); // TODO FG: remove this when the TODO above will be fixed
  //                         }
  //                         slots.splice(i, 1);
  //                     } else {
  //                         i++;
  //                     }
  //                 }
  //
  //                 segments.push({
  //                     time: _date,
  //                     timeStr: this._datePipe.transform(_date, 'shortTime', undefined, this.translater.currentLang) ?? '',
  //                     armyTimeStr: this._utils.DateToHm(_date),
  //                     slots: slotsData
  //                 });
  //
  //                 // we add fake segments for the winter/summer time change
  //                 const timezoneOffset = _date.getTimezoneOffset() - _toDate.getTimezoneOffset();
  //                 if (timezoneOffset > 0) {
  //                     for (i = 0; i < timezoneOffset / 60 * timeSegments; i++) {
  //                         segments.push({
  //                             time: _date,
  //                             timeStr: this._datePipe.transform(_date, 'shortTime', undefined, this.translater.currentLang) ?? '',
  //                             armyTimeStr: this._utils.DateToHm(_date),
  //                             slots: []
  //                         });
  //                     }
  //                 }
  //
  //                 _date = _toDate;
  //             }
  //
  //             resolve({
  //                 date,
  //                 segments,
  //                 timeSegments
  //             });
  //         })
  //         .catch(reject);
  // })
  // .catch(reject);
  // });
  // }

  public updateTimeslotDate({id, date}: {id: number, date: string}): Observable<{success: boolean, error: string | null, timeslot: any}> {
    return this.api.call('patch', 'timeslots/' + id, {fromDate: date}, this._currentUser.getAccessToken()).pipe(
      map((result: any) => {
        return {
          success: true,
          error: null,
          timeslot: result,
        }
      }),
      catchError((error) => {
        switch (error.status) {
          case 422:
            return of({
              success: false,
              error: 'validation',
              timeslot: null,
            });
          default:
            return of({
              success: false,
              error: error.message,
              timeslot: null,
            });
        }
      }),
    );
  }

  public saveCalendarWeek(organization: Organization, agenda: Agenda, slots: Timeslot[], from: string, to: string, agendaComment: AgendaCommentRow = {comment: ''} as AgendaCommentRow): Observable<Timeslot[]> {
    /**
     * J'ajoute aux "slots" les slots qui ont été répétés sur les
     * prochaines semaines. Je passe également à l'API le tableau
     * des tags de répétition qui doivent être supprimés.
     */
    const repSlots = this._timeslotsChangesProvider.getRepeatedTimeslots();
    if (repSlots) {
      slots = slots.concat(repSlots);
    }

    return this.api.call(            'post',
      'organizations/' + organization.id + '/agendas/' + agenda.id + '/interval',
      {
        intervalFrom: from,
        intervalTo: to,
        slots: this._utils.buildModelParams(slots, this._langsProvider.getLangIds()),
        removedTags: this._timeslotsChangesProvider.removedRepetitionTags,
        agendaComment
      },
      this._currentUser.getAccessToken()
    ).pipe(
      map((data: any) => {
        this._timeslotsChangesProvider.resetAll();
        slots = Timeslot.fromAPIList(data.timeslots);
        return data;
      }),
    );
  }

  /**
   * Retourne les répétitions hebdomadaires entre ces 2 dates
   * @param startDate
   * @param endDate
   * @param duration
   * @param unit
   */
  public getRepetitionTimeslots(startDate: Date, endDate: Date, duration: number, unit: string): Date[] {

    const ret = [];
    const ed = moment(endDate);
    const sd = moment(startDate);
    // @ts-ignore
    sd.add(duration, unit);

    while (sd.valueOf() < ed.valueOf()) {
      ret.push(new Date(sd.valueOf()));
      // @ts-ignore
      sd.add(duration, unit);
    }

    return ret;
  }

  /**
   * Vérifie si certains slots sont déjà occupés.
   * @param agendaId
   * @param day
   * @param sessionRow
   * @param timeslotsOfTheDay
   * @param sessionsOfTheDay
   */
  public canDropSlot(agendaId: number, day: Date, sessionRow: SessionRow, timeslotsOfTheDay: Timeslot[], sessionsOfTheDay: Session[]) {

    // détermine les indexes occupés par les timeslots existants
    let busyIndexes: number[] = [];
    for (let i = 0; i < timeslotsOfTheDay.length; i++) {
      if (!timeslotsOfTheDay[i].fromDate || sessionsOfTheDay[i].totalTime) return;
      busyIndexes = busyIndexes.concat(this.getBusyTimeIndexes(timeslotsOfTheDay[i].fromDate!.substr(11, 5), sessionsOfTheDay[i].totalTime!));
    }

    // détermine les indexes occupés par le nouveau timeslot que j'essaie de créer
    const hopeIndexes: number[] = this.getBusyTimeIndexes(this._utils.DateToHm(day), sessionRow.totalTime!);

    // est-ce que l'intersection contient quelque chose ?
    // https://medium.com/@alvaro.saburido/set-theory-for-arrays-in-es6-eb2f20a61848
    // @ts-ignore
    const intersection = busyIndexes.filter((x) => hopeIndexes.includes(x));

    // true si vide
    return (intersection.length === 0);
  }

  public getTimeSlotsByWeek(date: Date = new Date()): Observable<any> {
    const startOfWeek = moment(date).startOf('isoWeek');
    const endOfWeek = moment(date).endOf('isoWeek');

    const dates: string[] = [startOfWeek.format('YYYY-MM-DD') + ' 00:00:00', endOfWeek.format('YYYY-MM-DD') + ' 23:59:59'];

    return forkJoin([
      this.getSessionsByTimeslotDates(dates),
      //this.getTimeslotsByAthlete({athleteUserId: athleteUserId, fromDate: dates[0], toDate: dates[1]})
    ]).pipe(
      map((data: any) => {
        const sessionTimeSlotRows: SessionTimeSlotRow[] = [];
        const timeslots = data[1]?.length > 0 ? data[1] : [];

        if (data[0].length > 0) {
          const sessions = data[0];
          sessions.forEach((session: Session) => {
            const timeslot = this._utils.findIn(session.id, timeslots, 'sessionId');

            if (timeslot) {

              const dte = moment(timeslot.fromDate);
              const stsr: SessionTimeSlotRow = {
                id: session.id!,
                comment: session.comment,
                adn: session.adn,
                name: session.name!,
                totalTime: session.totalTime!,
                timeslotId: timeslot.id,
                fromDate: dte.format('YYYY-MM-DD HH:mm:ss'),
                day: dte.format('YYYY-MM-DD')
              };
              sessionTimeSlotRows.push(stsr);
            }
          });
        }

        sessionTimeSlotRows.sort((a: SessionTimeSlotRow, b: SessionTimeSlotRow) => this._utils.sortRow(a.fromDate, b.fromDate));


        return sessionTimeSlotRows;
      }),
    );
  }

  /**
   * Détermine les indexes occupés
   * @param startHour
   * @param duration
   */
  private getBusyTimeIndexes(startHour: string, duration: number) {

    const ret: number[] = [];
    const slotDuration: number = 3600 / this.timeSegments;
    const slotDurationM: number = 3600 / this.timeSegments / 60;
    const hour = startHour.split(':');
    const h = parseInt(hour[0], 10);
    const m = parseInt(hour[1], 10);

    // Echelle du temps pour des segments de 30 minutes :
    // Heure : 0       1       2       3       4       5       6       7       8
    //         |   |   |   |   |   |   |   |   |   |   |   |   |   |   |   |   |
    // Index : 0   1   2   3   4   5   6   7   8   9   10  11  12  13  14  15  16
    //
    //
    // Echelle du temps pour des segments de 15 minutes :
    // Heure : 0               1               2               3               4
    //         |   |   |   |   |   |   |   |   |   |   |   |   |   |   |   |   |
    // Index : 0   1   2   3   4   5   6   7   8   9   10  11  12  13  14  15  16

    // index de départ selon l'heure reçues
    // si this.timeSegments = 2 (30m)
    //      lorsque 08h => 8 * 2 = 16
    // si this.timeSegments = 4 (15m)
    //      lorsque 08h => 8 * 4 = 32
    const indexHour = h * this.timeSegments;

    // index de départ selon les minutes reçues
    // si slotDurationM = 30m :
    //      lorsque 00m =>  0 / 30 = 0
    //      lorsque 30m => 30 / 30 = 1
    // si slotDurationM = 15m :
    //      lorsque 00m =>  0 / 15 = 0
    //      lorsque 15m => 15 / 15 = 1
    //      lorsque 30m => 30 / 15 = 2
    //      lorsque 45m => 45 / 15 = 3
    const indexMinute = parseInt((m / slotDurationM).toString(), 10);

    // index de départ pour les slots occupés
    const startBusyIndex = indexHour + indexMinute;

    // nombre de slot selon la durée "duration"
    // si slotDuration = 1800s (30m)
    //      lorsque duration = 25m = 1500s => 1500 / 1800 = 0
    //      lorsque duration = 45m = 2700s => 2700 / 1800 = 1
    // si slotDuration = 900s (15m)
    //      lorsque duration = 25m = 1500s => 1500 / 900 = 1
    //      lorsque duration = 45m = 2700s => 2700 / 900 = 3
    const nbSlots = parseInt((duration / slotDuration).toString(), 10);

    // nombre de secondes restantes pour déterminer un dernier slot occupé
    // si slotDuration = 1800s (30m)
    //      lorsque duration = 25m = 1500s => 1500 % 1800 = 0       <= aucun slot supplémentaire
    //      lorsque duration = 45m = 2700s => 2700 % 1800 = 900     <= 1 slot suplémentaire
    // si slotDuration = 900s (15m)
    //      lorsque duration = 25m = 1500s => 1500 % 900 = 600     <= 1 slot suplémentaire
    //      lorsque duration = 45m = 2700s => 2700 & 900 = 0       <= aucun slot supplémentaire
    const modSeconds = duration % slotDuration;

    ret.push(startBusyIndex);

    // le premier slot est déjà dans la table, on commence donc à 0
    let i = 0;
    if (nbSlots) {
      for (i = 0; i < nbSlots; i++) {
        if (ret.indexOf(startBusyIndex + i) < 0) {
          ret.push(startBusyIndex + i);
        }
      }
    }
    if (modSeconds > 0) {
      if (ret.indexOf(startBusyIndex + i) < 0) {
        ret.push(startBusyIndex + i);
      }
    }

    return ret;
  }
}

export interface SessionTimeSlotRow {
  id: number;
  comment?: string;
  adn?: string;
  totalTime: number;
  name: string;
  fromDate: string;
  timeslotId: number;
  day: string;
}

export interface TimeslotRow {
  timeslot: Timeslot | null;
  session: SessionRow | null;
  calendarGeneratedId?: any;
  action?: string | null; // TODO v�rifier si c'est vraiment utile ...
}

export interface CalendarDayData {
  date: Date;
  segments: CalendarDaySegment[];
  timeSegments: number;
}

export interface CalendarDaySegment {
  time: Date;
  timeStr: string;
  armyTimeStr: string;
  slots: TimeslotRow[];
  displayTime?: boolean;
}

export interface AgendaRow {
  agenda: Agenda;
  rights: AgendaItemRightsList;
}

export interface WeekForDuplication {
  name: string;
  number: number;
  fromDate: string;
}
